import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";
import { useOrg } from "./session";

// Each person's dashboard preferences in one company, and the company details.

export type Theme = "light" | "dark" | "system";
export interface Folder { id: string; name: string; items: string[]; collapsed: boolean }
export type Entry = { item: string; folder?: undefined } | { item?: undefined; folder: Folder };
export interface Prefs { theme: Theme; sidebar: Entry[]; hidden?: string[] }

export interface Company {
  display_name: string; legal_name: string; website: string | null; phone: string | null; public_email: string | null; address: string | null;
  dashboard_brand_color: boolean; logo_url: string; accent?: string; on_accent?: string; can_edit: boolean; is_owner: boolean;
}

export function usePrefs() {
  const { base, orgId } = useOrg();
  const qc = useQueryClient();
  const key = [orgId, "preferences"];
  const q = useQuery({ queryKey: key, queryFn: () => api<Prefs>(`${base}/preferences`), staleTime: 5 * 60_000 });
  const save = async (next: Prefs) => {
    const prev = qc.getQueryData<Prefs>(key);
    qc.setQueryData(key, next);
    try {
      qc.setQueryData(key, await api<Prefs>(`${base}/preferences`, { method: "PUT", json: next }));
    } catch (e) {
      qc.setQueryData(key, prev);
      throw e;
    }
  };
  return { prefs: q.data ?? { theme: "dark" as Theme, sidebar: [], hidden: [] }, loaded: !!q.data, save };
}

export function useCompany() {
  const { base, orgId } = useOrg();
  return useQuery({ queryKey: [orgId, "company"], queryFn: () => api<Company>(`${base}/company`), staleTime: 5 * 60_000 });
}

const hex = /^#[0-9a-fA-F]{6}$/;

/** useAppearance applies the company's brand colour to the dashboard's main
 *  buttons when the company turned that on. There is one dark theme. */
export function useAppearance(accent?: string, onAccent?: string) {
  useEffect(() => {
    const root = document.documentElement;
    if (accent && hex.test(accent)) {
      root.style.setProperty("--primary", accent);
      root.style.setProperty("--on-primary", onAccent && hex.test(onAccent) ? onAccent : "#ffffff");
    }
    return () => { root.style.removeProperty("--primary"); root.style.removeProperty("--on-primary"); };
  }, [accent, onAccent]);
}

// The default sidebar: the day-to-day pages first, in the order people use
// them (Today, Inbox, contacts, deals, tasks, calendar), then the AI pages in
// one folder, then Results. Setup pages are under Settings, not here. People
// rearrange it in Settings → Sidebar; this is what they start with.
const defaultOrder = ["today", "inbox", "contacts", "deals", "tasks", "calendar"];
const defaultFolders: { id: string; name: string; keys: string[] }[] = [
  { id: "ai", name: "AI", keys: ["agents", "knowledge", "automations"] },
];
const defaultAfter = ["results"];

/** defaultLayout is the starting sidebar for the given pages. */
export function defaultLayout<T extends { key: string; label: string }>(items: T[]): Entry[] {
  const has = new Set(items.map((i) => i.key));
  const out: Entry[] = defaultOrder.filter((k) => has.has(k)).map((k) => ({ item: k }));
  for (const f of defaultFolders) {
    const keys = f.keys.filter((k) => has.has(k));
    if (keys.length) out.push({ folder: { id: f.id, name: f.name, items: keys, collapsed: false } });
  }
  for (const k of defaultAfter) if (has.has(k)) out.push({ item: k });
  return out;
}

// Folders the old default sidebar made (before setup pages moved to Settings).
// A sidebar saved with them follows the old, confusing order, so it starts
// again from the new default.
const oldDefaultFolders = new Set(["bookings", "company", "talk", "sales"]);

/** layoutFor is the person's saved sidebar, or the default one. */
export function layoutFor<T extends { key: string; label: string }>(saved: Entry[] | undefined, items: T[]): Entry[] {
  if (!saved || !saved.length || saved.some((e) => e.folder && oldDefaultFolders.has(e.folder.id))) return defaultLayout(items);
  return saved;
}

/** arrange puts the visible sidebar items in the person's order: their
 *  entries first (skipping pages they can't see), then any pages they haven't
 *  placed yet, in the default order. Empty folders are dropped. */
export function arrange<T extends { key: string }>(items: T[], layout: Entry[]): ({ item: T } | { folder: Folder; items: T[] })[] {
  const byKey = new Map(items.map((i) => [i.key, i]));
  const placed = new Set<string>();
  const out: ({ item: T } | { folder: Folder; items: T[] })[] = [];
  for (const e of layout) {
    if (e.item !== undefined) {
      const it = byKey.get(e.item);
      if (it && !placed.has(it.key)) { out.push({ item: it }); placed.add(it.key); }
    } else if (e.folder) {
      const inside = e.folder.items.map((k) => byKey.get(k)).filter((x): x is T => !!x && !placed.has(x.key));
      inside.forEach((x) => placed.add(x.key));
      if (inside.length) out.push({ folder: e.folder, items: inside });
    }
  }
  // Pages added since the layout was saved go next to their neighbour in the
  // default order (e.g. a new tab right after Calendar), not at the bottom.
  items.forEach((it, i) => {
    if (placed.has(it.key)) return;
    let at = out.length;
    for (let j = i - 1; j >= 0; j--) {
      const idx = out.findIndex((o) => "item" in o && o.item.key === items[j].key);
      if (idx >= 0) { at = idx + 1; break; }
    }
    out.splice(at, 0, { item: it });
    placed.add(it.key);
  });
  return out;
}
